import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Chip } from "../components/common";
import SimpleCrud from "../components/settings/SimpleCrud";
import Tariffs from "../components/settings/Tariffs";
import { ExcelPanel, PeriodsPanel } from "../components/settings/Panels";
import { ROLE_LABELS, VAT_MODES, TOURE_GROUPS } from "../lib/labels";
import { NewSubcontractorButton } from "../components/AdminActions";
import { LoginHistory } from "../components/settings/LoginHistory";
import { useLang } from "../lib/i18n";
import { useFocusParam } from "../hooks/useFocusParam";
import { SplitRulesPanel } from "../components/Split";
import { PendingAccounts } from "../components/settings/PendingAccounts";

const Yes = ({ v }) => {
  const { tr } = useLang();
  return v ? <Chip tone="green">{tr("oui")}</Chip> : <Chip>{tr("non")}</Chip>;
};
const Revoke = () => {
  const { tr } = useLang();
  return tr("Révoquer");
};
const yes = (v) => <Yes v={v} />;



export default function Settings() {
  const { user } = useAuth();
  const { t } = useLang();
  const isAdmin = user.role === "admin";
  const focus = useFocusParam(["tab", "export"]);
  const [tab, setTab] = useState(focus.tab || "users");
  useEffect(() => {
    if (focus.tab) setTab(focus.tab);
  }, [focus.tab, focus.export]);
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, [tab, tick]);
  const cOpts = [["", "—"], ...companies.map((c) => [c.id, c.name])];
  const dOpts = [["", "—"], ...drivers.map((d) => [d.id, `${d.name} (${companies.find((c) => c.id === d.company_id)?.name || "?"})`])];
  const cName = (id) => companies.find((c) => c.id === id)?.name || "";
  const dName = (id) => drivers.find((d) => d.id === id)?.name || "";
  const roleOpts = Object.entries(ROLE_LABELS).filter(([k]) => isAdmin || ["soustraitant", "chauffeur"].includes(k));
  const tabs = [
    ["users", "Utilisateurs"], ...(isAdmin ? [["companies", "Sociétés"]] : []), ["drivers", "Chauffeurs"], ...(isAdmin ? [["logins", "Connexions"], ["split", "Répartition"]] : []),
    ["assignments", "Affectations"], ["tariffs", "Matrice tarifaire"], ["periods", "Clôtures"], ["excel", "Excel UPS"],
  ];
  return (
    <div>
      <PageTitle title={t("set.title")} sub={t("set.sub")} />
      <div className="flex gap-1 flex-wrap mb-5" role="tablist">
        {tabs.map(([k]) => [k, t(`tab.${k}`)]).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`tl-btn tl-btn-sm ${tab === k ? "tl-btn-dark" : "tl-btn-ghost"}`} data-testid={`settings-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "users" && <PendingAccounts isAdmin={isAdmin} onApproved={() => setTick(tick + 1)} />}
      {tab === "users" && <NewSubcontractorButton onSaved={() => setTick(tick + 1)} />}
      {tab === "users" && (
        <SimpleCrud key={tick} endpoint="/users" title="users" canEdit
          defaults={{ email: "", name: "", role: "soustraitant", password: "", company_id: "", driver_id: "", can_edit_tariffs: false, active: true }}
          fields={[{ key: "email", label: "E-mail", type: "email" }, { key: "name", label: "Nom" },
            { key: "role", label: "Profil", type: "select", options: roleOpts },
            { key: "password", label: "Mot de passe (8 car. min., vide = inchangé)", type: "password" },
            { key: "company_id", label: "Société", type: "select", options: cOpts }, { key: "driver_id", label: "Chauffeur (profil chauffeur)", type: "select", options: dOpts },
            { key: "can_edit_tariffs", label: "Autorisé à modifier les tarifs", type: "checkbox" }, { key: "active", label: "Compte actif", type: "checkbox" }]}
          columns={[["email", "E-mail"], ["name", "Nom"], ["role", "Profil", (v) => ROLE_LABELS[v]], ["company_id", "Société", cName], ["active", "Actif", yes]]}
          rowActions={(r, load) => <button className="tl-btn tl-btn-danger tl-btn-sm" onClick={async () => { await api.post(`/users/${r.id}/revoke`); toast.success("Sessions révoquées"); load(); }} data-testid={`revoke-${r.id}`}><Revoke /></button>} />
      )}
      {tab === "companies" && (
        <SimpleCrud endpoint="/companies" title="companies" canEdit={isAdmin}
          defaults={{ name: "", vat_number: "", manager_name: "", email: "", sheet_name: "", vat_mode: "a_confirmer", vat_rate: null, active: true, validated: false, notes: "" }}
          fields={[{ key: "name", label: "Nom" }, { key: "vat_number", label: "Numéro de TVA" }, { key: "manager_name", label: "Nom du gérant" }, { key: "email", label: "E-mail de la société" }, { key: "sheet_name", label: "Feuille Excel (ex. KENAN)" },
            { key: "vat_mode", label: "Traitement TVA", type: "select", options: Object.entries(VAT_MODES) }, { key: "vat_rate", label: "Taux TVA %", type: "number" },
            { key: "validated", label: "Société validée (feuille Excel)", type: "checkbox" }, { key: "active", label: "Active", type: "checkbox" }, { key: "notes", label: "Notes" }]}
          columns={[["name", "Nom"], ["vat_number", "TVA n°"], ["manager_name", "Gérant"], ["sheet_name", "Feuille"], ["vat_mode", "TVA", (v) => VAT_MODES[v]], ["validated", "Validée", yes], ["active", "Active", yes]]} />
      )}
      {tab === "drivers" && (
        <SimpleCrud endpoint="/drivers" title="drivers" canEdit
          defaults={{ company_id: "", name: "", employee_id: "", toure_group: "", active: true }}
          fields={[{ key: "company_id", label: "Société", type: "select", options: cOpts }, { key: "name", label: "Nom" },
            { key: "employee_id", label: "Employee ID habituel" }, { key: "toure_group", label: "Feuillet Excel TOURE (groupe de chauffeurs, sans lien avec le compte admin)", type: "select", options: TOURE_GROUPS.map((g) => [g, g || "Aucun"]) },
            { key: "active", label: "Autorisé", type: "checkbox" }]}
          columns={[["name", "Nom"], ["company_id", "Société", cName], ["employee_id", "Employee ID"], ["toure_group", "Feuillet TOURE"], ["active", "Autorisé", yes]]} />
      )}
      {tab === "logins" && <LoginHistory />}
      {tab === "split" && isAdmin && <SplitRulesPanel />}
      {tab === "assignments" && (
        <SimpleCrud endpoint="/assignments" title="assignments" canEdit={isAdmin}
          defaults={{ driver_id: "", employee_id: "", route_id: "", valid_from: "", valid_to: "", active: true }}
          fields={[{ key: "driver_id", label: "Chauffeur", type: "select", options: dOpts }, { key: "employee_id", label: "Employee ID (Diad)" },
            { key: "route_id", label: "Route ID" }, { key: "valid_from", label: "Du", type: "date" }, { key: "valid_to", label: "Au", type: "date" }, { key: "active", label: "Active", type: "checkbox" }]}
          columns={[["employee_id", "Employee ID"], ["driver_id", "Chauffeur", dName], ["company_id", "Société", cName], ["route_id", "Route"], ["valid_from", "Du"], ["valid_to", "Au"], ["active", "Active", yes]]} />
      )}
      {tab === "tariffs" && <Tariffs />}
      {tab === "periods" && <PeriodsPanel isAdmin={isAdmin} />}
      {tab === "excel" && <ExcelPanel focusId={focus.export} />}
    </div>
  );
}
